import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { IntegrationProperty } from '../../shared/models/integration-property';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { CustomValidators } from 'app/shared';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { Integration } from '../../shared/models/integration';
import { IntegrationCredential } from '../../shared/models/integration-credential';
import { CreateAdminIntegrationCredentialAction, CreateAdminIntegrationPropertyAction, DownloadAssociationListAction, LoadAdminIntegrationPropertyAction, LoadAdminIntegrationsAction, UpdateAdminIntegrationPropertyAction } from '../../admin-store/admin.actions';
import { select, Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import { getIntegrationCredential, getIntegrationProperty, getIntegrations } from '../../admin-store/admin.selectors';
import { AppState } from 'app/app.reducers';
import { WorkspacePropertyIntegrationDetail } from 'app/internal/shared/models/workspace-property';
import { RunSyncScheduleRequest } from 'app/internal/connected-apps/models/run-sync-schedule-request';
import * as ConnectedAppsActions from 'app/internal/connected-apps/store/connected-apps.actions';

@Component({
  selector: 'sp-add-connected-app-modal',
  templateUrl: './add-connected-app-modal.component.html',
  styleUrls: ['./add-connected-app-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false
})
export class AddConnectedAppModalComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild(ModalDirective, { static: false }) public bsModal: ModalDirective;

  @Input() propertyId: string;
  @Input() propertyName: string = '';
  @Input() integrationStatus: WorkspacePropertyIntegrationDetail = null;
  
  integrationProperty$: Observable<IntegrationProperty>;
  integrationCredential$: Observable<IntegrationCredential>;
  integrations$: Observable<Integration[]>;
  
  integrationProperty: IntegrationProperty = null;
  integrationCredential: IntegrationCredential = null;
  integrations: Integration[] = [];

  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  showAddCredential = false;
  showDownloadAssociations = false;
  showConfig = false;
  showStatusDetail = false;

  private subs = new SubSink();

  constructor(private store: Store<AppState>,
    private fb: UntypedFormBuilder,
  ) {
    this.integrationProperty$ = this.store.pipe(select(getIntegrationProperty));
    this.integrationCredential$ = this.store.pipe(select(getIntegrationCredential));
    this.integrations$ = this.store.pipe(select(getIntegrations));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertyId'] && this.propertyId) {
      this.formGroup = new UntypedFormGroup({});
      this.integrationProperty = null; //reset the integration property, just in case.
      this.store.dispatch(new LoadAdminIntegrationPropertyAction(this.propertyId));
    }
  }

  show(): void {
    this.bsModal.show();
  }

  ngOnInit() {
    
    this.subs.sink = this.integrationProperty$.subscribe(integrationProperty => {
      if (integrationProperty) {
        this.integrationProperty = integrationProperty;
        this.showDownloadAssociations = this.integrationProperty?.integrationCredentialId ? true : false;
      }
    });

    this.subs.sink = this.integrationCredential$.subscribe(integrationCredential => {
      if (integrationCredential) {
        this.integrationCredential = integrationCredential;
      }
    });

    this.subs.sink = this.integrations$.subscribe(integrations => {
      if (integrations) {
        this.integrations = integrations;
      }
    });
    //only need to load this one once
    this.store.dispatch(new LoadAdminIntegrationsAction());
    //initialize the form
    this.initForm();
  }

  ngAfterViewInit(): void {
    this.bsModal.config.keyboard = false;
    this.bsModal.config.ignoreBackdropClick = true;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  closeModal(): void {
    this.propertyId = null;
    this.integrationProperty = null;
    this.integrationCredential = null;
    this.showDownloadAssociations = false;
    this.propertyName = '';
    this.integrationStatus = null;
    this.showConfig = false;
    this.showStatusDetail = false;
    this.bsModal.hide();
  }

  toggleAddCredential() {
    this.showAddCredential = !this.showAddCredential;
  }

  showConfigTab(showConfig: boolean) {
    this.showConfig = showConfig;
    
    if (showConfig) {
      this.initForm();
    }
  }

  syncIntegration() {
    if (this.integrationProperty) {
      this.integrationStatus.lastUpdateStatus = 1; //set to in progress
      const request = new RunSyncScheduleRequest(this.integrationProperty.integrationPropertyId);
      this.store.dispatch(ConnectedAppsActions.runPropertySyncSchedule({ runSyncScheduleRequest: request }));
    } 
  }

  resetGlCodesByRules() {
    if (this.integrationProperty) {
      this.store.dispatch(ConnectedAppsActions.resetLedgerCodesByRules({ propertyId: this.propertyId }));
    } 
  }

  enableSave(): boolean {
    return !this.showAddCredential;
  }

  save() {
    const controls = this.formGroup.controls;
    const updatedIntegrationProperty = this.integrationProperty == null ? <IntegrationProperty>{} : this.integrationProperty;

    updatedIntegrationProperty.integrationCredentialId = controls['integrationCredentialId'].value;
    updatedIntegrationProperty.externalPropertyCode = controls['externalPropertyCode'].value;
    updatedIntegrationProperty.useReserveFundsForExpenses = this.integrationProperty == null ? false : this.integrationProperty.useReserveFundsForExpenses;
    updatedIntegrationProperty.useReserveFundsForIncome = this.integrationProperty == null ? false : this.integrationProperty.useReserveFundsForIncome;

    // if we are creating the integration property & if its vantaca (integrationId == 2),
    // then set useReserveFunds = true, otherwise let them default to false
    const integrationId = this.formGroup?.controls ? this.formGroup?.controls['integrationId'].value : null;
    if (this.integrationProperty == null && integrationId == 2) {
      updatedIntegrationProperty.useReserveFundsForExpenses = true;
      updatedIntegrationProperty.useReserveFundsForIncome = true;
    }

    if (updatedIntegrationProperty.integrationPropertyId) {
      this.store.dispatch(new UpdateAdminIntegrationPropertyAction({ propertyId: this.propertyId, integrationProperty: updatedIntegrationProperty }));
    } else {
      updatedIntegrationProperty.propertyId = this.propertyId;
      this.store.dispatch(new CreateAdminIntegrationPropertyAction({ propertyId: this.propertyId, integrationProperty: updatedIntegrationProperty }));
    }

    this.closeModal();
  }

  onAddCredential(credential: IntegrationCredential) {
    const controls = this.formGroup?.controls;
    credential.integrationId = controls['integrationId'].value;
    this.store.dispatch(new CreateAdminIntegrationCredentialAction(credential));
    this.showAddCredential = false;
  }

  onCancelAddCredential() {
    this.showAddCredential = false;
  }

  get integrationCredentials() {
    const controls = this.formGroup?.controls;
    if (controls) {
      const selectedIntegrationId = controls['integrationId']?.value;
      const selectedIntegration = this.integrations.find(x => x.integrationId == selectedIntegrationId);
      return selectedIntegration ? selectedIntegration.integrationCredentials : [];
    }
  }

  onCredentialChange(credentialId) {
    this.showDownloadAssociations = credentialId ? true : false;
  }

  onDownloadAssociationList() {    
    const controls = this.formGroup?.controls;
    if (controls) {
      const integrationId = controls['integrationId'].value;
      const integrationCredentialId = controls['integrationCredentialId'].value;
      this.store.dispatch(new DownloadAssociationListAction({ integrationId, integrationCredentialId }));
    }
  }

  get integrationId() {
    return this.formGroup?.controls ? this.formGroup?.controls['integrationId']?.value : null;
  }

  private initForm() {
    const integrationProperty = this.integrationProperty == null ? <IntegrationProperty>{} : this.integrationProperty;
    
    this.formGroup = new UntypedFormGroup({});
   
    this.formGroup.addControl('integrationId', this.fb.control(integrationProperty.integrationId,
      Validators.compose([
        Validators.required
      ])));

    this.formGroup.addControl('integrationCredentialId', this.fb.control(integrationProperty.integrationCredentialId,
      Validators.compose([
        Validators.required,
        CustomValidators.notEmptyStringValidator,
      ])));

    this.formGroup.addControl('externalPropertyCode', this.fb.control(integrationProperty.externalPropertyCode,
      Validators.compose([
        Validators.required,
        CustomValidators.notEmptyStringValidator,
      ])));

    if (this.integrationCredential != null   && this.integrationCredential.integrationCredentialId) {
      this.formGroup.controls['integrationCredentialId'].setValue(this.integrationCredential.integrationCredentialId);
    }
  }

}
